import { isEqual } from 'underscore';
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { SearchCriteriaHistory } from '@/entities/searchCriteria';
import { getCountryResorts } from '@/shared/api/getCountryResorts';
import { getOptions } from '@/shared/api/getOptions';
import type { SwipeDirection } from '@/shared/composables';
import type { Country, Destination, Hotel, Resort } from '@/shared/model';
import {
  removeSelectionItem,
  selectionChips,
  selectionCount,
  toggleAllResortsMobile,
  toggleHotelMobile,
  toggleResortMobile,
} from './mobileSelection';
import {
  getSelectedCountry,
  getSelectedHotels,
  isCountry,
  isHotels,
  isNone,
  selectCountry,
  type Selection,
} from './selection';
import type {
  DestinationMobileAnchor,
  DestinationMobileConfirmState,
  DestinationMobileMode,
  DestinationMobileSearchSections,
  DestinationMobileView,
  OptionGroup,
  SelectionChip,
} from './types';
import { DESTINATION_MOBILE_TEXTS, DESTINATION_TEXTS } from '../config/texts';
import { countryResortCatalog, resolveCountryFromList } from '../lib/countryLookup';
import { matchesDestinationName } from '../lib/matchDestinationName';
import { sortOptions } from '../lib/sortOptions';

export interface UseMobileDestinationOptions {
  model: Ref<Selection>;
  countries: Ref<Country[]>;
  history?: Ref<SearchCriteriaHistory[]>;
  hasSwitchedCountry?: Ref<boolean>;
  onSwitchedCountry?: () => void;
  searchUrl?: Ref<string | undefined>;
  resortsUrl?: Ref<string | undefined>;
}

const SEARCH_DEBOUNCE_MS = 300;
const HOTEL_SEARCH_MIN_CHARS = 3;
const RECENT_COUNTRIES_LIMIT = 3;

export function useMobileDestination(options: UseMobileDestinationOptions) {
  const texts = DESTINATION_MOBILE_TEXTS;

  const model = options.model;
  let lastEmitted: Selection | undefined = undefined as unknown as Selection;
  const selectionRef = ref<Selection>(model.value);
  const setSelection = (next: Selection): void => {
    lastEmitted = next;
    selectionRef.value = next;
    model.value = next;
  };

  watch(
    model,
    (value: Selection) => {
      if (!value || isEqual(value, lastEmitted)) {
        return;
      }
      selectionRef.value = value;
    },
    { immediate: true },
  );

  const view = ref<DestinationMobileView>('root');
  const mode = ref<DestinationMobileMode>('resorts');
  const direction = ref<SwipeDirection>('right');
  const openedCountry = ref<Country | null>(null);
  const query = ref('');
  const loading = ref(false);
  const searchLoading = ref(false);
  const countryResorts = ref<Resort[]>([]);
  const searchResults = ref<Destination[]>([]);
  const hotelResults = ref<Hotel[]>([]);
  const anchor = ref<DestinationMobileAnchor | null>(null);
  const confirmState = ref<DestinationMobileConfirmState | null>(null);
  let pendingAction: (() => void) | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  const selection = computed<Selection>(() => selectionRef.value);
  const lockedCountry = computed<Country | null>(() => getSelectedCountry(selection.value));
  const count = computed<number>(() => selectionCount(selection.value));
  const chips = computed<SelectionChip[]>(() => selectionChips(selection.value));

  const normalizedQuery = computed<string>(() => query.value.trim().toLowerCase());

  const placeholder = computed<string>(() => {
    if (view.value !== 'country') {
      return texts.searchPlaceholder;
    }
    return mode.value === 'hotels' ? texts.searchHotelsPlaceholder : texts.searchResortsPlaceholder;
  });

  const recentCountries = computed<Country[]>(() => {
    const known = new Map<number, Country>(options.countries.value.map((country) => [country.id, country]));
    const result: Country[] = [];
    for (const item of options.history?.value ?? []) {
      const country = known.get(item.countries[0]);
      if (country && !result.some((existing) => existing.id === country.id)) {
        result.push(country);
      }
      if (result.length >= RECENT_COUNTRIES_LIMIT) {
        break;
      }
    }
    const lock = lockedCountry.value;
    if (!lock) {
      return result;
    }
    return [lock, ...result.filter((country) => country.id !== lock.id)].slice(0, RECENT_COUNTRIES_LIMIT);
  });

  const countryGroups = computed<OptionGroup[]>(() =>
    sortOptions(
      options.countries.value.map(
        (country: Country): Destination => ({ type: 'country', country, resort: null, hotel: null }),
      ),
    ),
  );

  const searchSections = computed<DestinationMobileSearchSections>(() => {
    const countries: Country[] = [];
    const resorts: Resort[] = [];
    const hotels: Hotel[] = [];
    for (const item of searchResults.value) {
      if (item.type === 'country') {
        countries.push(item.country);
      } else if (item.type === 'resort') {
        resorts.push(item.resort);
      } else if (normalizedQuery.value.length >= HOTEL_SEARCH_MIN_CHARS) {
        hotels.push(item.hotel);
      }
    }
    return { countries, resorts, hotels };
  });

  const checkedResortIds = computed<Set<number>>(
    () => new Set(selection.value.kind === 'resorts' ? selection.value.resorts.map((resort) => resort.id) : []),
  );
  const checkedHotelIds = computed<Set<number>>(() => new Set(getSelectedHotels(selection.value).map((h) => h.id)));

  const allResortsChecked = computed<boolean>(
    () =>
      isCountry(selection.value) &&
      openedCountry.value !== null &&
      selection.value.country.id === openedCountry.value.id,
  );

  const resortGroups = computed<OptionGroup[]>(() => {
    if (view.value !== 'country' || mode.value !== 'resorts') {
      return [];
    }
    if (normalizedQuery.value === '') {
      return sortOptions(
        countryResorts.value.map((resort): Destination => ({ type: 'resort', country: null, resort, hotel: null })),
      );
    }
    const filtered = countryResorts.value.filter((resort) =>
      matchesDestinationName(resort.name, normalizedQuery.value),
    );
    if (filtered.length === 0) {
      return [];
    }
    return [
      {
        title: texts.groupResorts,
        kind: 'resort',
        items: filtered.map((resort): Destination => ({ type: 'resort', country: null, resort, hotel: null })),
      },
    ];
  });

  const hotelRows = computed<Hotel[]>(() => {
    if (view.value !== 'country' || mode.value !== 'hotels') {
      return [];
    }
    const selected = getSelectedHotels(selection.value);
    const term = normalizedQuery.value;
    if (term === '') {
      return selected;
    }
    const resultIds = new Set(hotelResults.value.map((hotel) => hotel.id));
    const pinnedSelected = selected.filter((hotel) => !resultIds.has(hotel.id));
    return [...pinnedSelected, ...hotelResults.value];
  });

  const isSearchEmpty = computed<boolean>(() => {
    if (normalizedQuery.value === '' || searchLoading.value) {
      return false;
    }
    if (view.value === 'root') {
      const sections = searchSections.value;
      return sections.countries.length === 0 && sections.resorts.length === 0 && sections.hotels.length === 0;
    }
    return mode.value === 'resorts' ? resortGroups.value.length === 0 : hotelRows.value.length === 0;
  });

  const catalogFor = (country: Country): Resort[] => countryResortCatalog(country, countryResorts.value);

  const resolveCountry = (countryId: number, fallbackName: string): Country =>
    resolveCountryFromList(countryId, fallbackName, lockedCountry.value, options.countries.value);

  const loadCountryResorts = async (country: Country): Promise<void> => {
    loading.value = true;
    const resorts = await getCountryResorts(country, options.resortsUrl?.value);
    if (openedCountry.value?.id === country.id) {
      countryResorts.value = resorts;
      const current = selection.value;
      const needsCatalog =
        !isNone(current) &&
        current.country.id === country.id &&
        (current.country.resorts === undefined || (current.country.resorts.length === 0 && resorts.length > 0));
      if (needsCatalog) {
        setSelection({ ...current, country: { ...current.country, resorts } });
      }
    }
    loading.value = false;
  };

  const clearSearchState = (): void => {
    if (searchTimer !== null) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    searchResults.value = [];
    hotelResults.value = [];
    searchLoading.value = false;
  };

  const runSearch = async (term: string): Promise<void> => {
    searchLoading.value = true;
    const results = await getOptions(term, options.countries.value, options.searchUrl?.value);
    if (normalizedQuery.value !== term) {
      return;
    }
    if (view.value === 'country') {
      const countryId = openedCountry.value?.id;
      hotelResults.value = results.flatMap((item) =>
        item.type === 'hotel' && item.hotel.countryId === countryId ? [item.hotel] : [],
      );
    } else {
      searchResults.value = results;
    }
    searchLoading.value = false;
  };

  const setQuery = (value: string): void => {
    query.value = value;
    if (searchTimer !== null) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    const term = normalizedQuery.value;
    if (term === '') {
      clearSearchState();
      return;
    }
    if (view.value === 'country' && mode.value === 'resorts') {
      return;
    }
    searchTimer = setTimeout(() => {
      searchTimer = null;
      runSearch(term);
    }, SEARCH_DEBOUNCE_MS);
  };

  const requestConfirm = (state: DestinationMobileConfirmState, action: () => void): void => {
    confirmState.value = state;
    pendingAction = action;
  };

  const confirmPending = (): void => {
    const action = pendingAction;
    pendingAction = null;
    confirmState.value = null;
    action?.();
  };

  const cancelPending = (): void => {
    pendingAction = null;
    confirmState.value = null;
  };

  const switchedCountry = ref(options.hasSwitchedCountry?.value ?? false);
  if (options.hasSwitchedCountry) {
    watch(options.hasSwitchedCountry, (value: boolean) => {
      switchedCountry.value = value;
    });
  }
  const markSwitchedCountry = (): void => {
    switchedCountry.value = true;
    options.onSwitchedCountry?.();
  };

  const switchCountryConfirm = (next: string, action: () => void): void => {
    if (!switchedCountry.value) {
      markSwitchedCountry();
      action();
      return;
    }
    requestConfirm(
      {
        title: texts.switchCountryTitle,
        text: texts.switchCountryText(lockedCountry.value?.name ?? '', next),
        confirmLabel: texts.switchCountryConfirm,
        cancelLabel: texts.cancel,
      },
      action,
    );
  };

  const switchModeConfirm = (name: string, toHotels: boolean, action: () => void): void => {
    requestConfirm(
      {
        title: toHotels ? texts.switchToHotelsTitle : texts.switchToResortsTitle,
        text: texts.switchModeText(name, toHotels),
        confirmLabel: toHotels ? texts.switchToHotelsConfirm : texts.switchToResortsConfirm,
        cancelLabel: texts.cancel,
      },
      action,
    );
  };

  const enterCountry = (country: Country, keepSelection: boolean): void => {
    direction.value = 'right';
    view.value = 'country';
    openedCountry.value = country;
    query.value = '';
    clearSearchState();
    if (!keepSelection || isNone(selection.value)) {
      setSelection(selectCountry(country, catalogFor(country)));
      mode.value = 'resorts';
    } else {
      mode.value = isHotels(selection.value) ? 'hotels' : 'resorts';
    }
    loadCountryResorts(country);
  };

  const openCountry = (country: Country): void => {
    const lock = lockedCountry.value;
    if (lock && lock.id !== country.id) {
      switchCountryConfirm(country.name, () => enterCountry(country, false));
      return;
    }
    enterCountry(country, lock?.id === country.id);
  };

  const applyResortPick = (resort: Resort, country: Country): void => {
    direction.value = 'right';
    view.value = 'country';
    openedCountry.value = country;
    mode.value = 'resorts';
    query.value = '';
    clearSearchState();
    if (!checkedResortIds.value.has(resort.id) || selection.value.kind !== 'resorts') {
      setSelection(toggleResortMobile(selection.value, resort, country));
    }
    anchor.value = { type: 'resort', id: resort.id };
    loadCountryResorts(country);
  };

  const applyHotelPick = (hotel: Hotel, country: Country): void => {
    direction.value = 'right';
    view.value = 'country';
    openedCountry.value = country;
    mode.value = 'hotels';
    query.value = '';
    clearSearchState();
    if (!checkedHotelIds.value.has(hotel.id)) {
      setSelection(toggleHotelMobile(selection.value, hotel, country));
    }
    anchor.value = { type: 'hotel', id: hotel.id };
  };

  const pickSearchResult = (destination: Destination): void => {
    if (destination.type === 'country') {
      openCountry(destination.country);
      return;
    }
    if (destination.type === 'resort') {
      const resort = destination.resort;
      const country = resolveCountry(resort.countryId, resort.countryName);
      if (lockedCountry.value && lockedCountry.value.id !== country.id) {
        switchCountryConfirm(country.name, () => applyResortPick(resort, country));
        return;
      }
      if (isHotels(selection.value)) {
        switchModeConfirm(resort.name, false, () => applyResortPick(resort, country));
        return;
      }
      applyResortPick(resort, country);
      return;
    }
    const hotel = destination.hotel;
    const country = resolveCountry(hotel.countryId, hotel.countryName);
    if (lockedCountry.value && lockedCountry.value.id !== country.id) {
      switchCountryConfirm(country.name, () => applyHotelPick(hotel, country));
      return;
    }
    if (!isNone(selection.value) && !isHotels(selection.value)) {
      switchModeConfirm(hotel.name, true, () => applyHotelPick(hotel, country));
      return;
    }
    applyHotelPick(hotel, country);
  };

  const toggleAllResorts = (): void => {
    const country = openedCountry.value;
    if (!country) {
      return;
    }
    const apply = (): void => {
      setSelection(toggleAllResortsMobile(selection.value, country, catalogFor(country)));
    };
    if (!allResortsChecked.value && isHotels(selection.value)) {
      switchModeConfirm(DESTINATION_TEXTS.selectAll, false, apply);
      return;
    }
    apply();
  };

  const toggleResort = (resort: Resort): void => {
    const country = resolveCountry(resort.countryId, resort.countryName);
    const apply = (): void => {
      setSelection(toggleResortMobile(selection.value, resort, country));
    };
    if (checkedResortIds.value.has(resort.id)) {
      apply();
      return;
    }
    const lock = lockedCountry.value;
    if (lock && lock.id !== country.id) {
      switchCountryConfirm(country.name, apply);
      return;
    }
    if (isHotels(selection.value)) {
      switchModeConfirm(resort.name, false, apply);
      return;
    }
    apply();
  };

  const toggleHotel = (hotel: Hotel): void => {
    const country = resolveCountry(hotel.countryId, hotel.countryName);
    const apply = (): void => {
      setSelection(toggleHotelMobile(selection.value, hotel, country));
    };
    if (checkedHotelIds.value.has(hotel.id)) {
      apply();
      return;
    }
    const lock = lockedCountry.value;
    if (lock && lock.id !== country.id) {
      switchCountryConfirm(country.name, apply);
      return;
    }
    if (!isNone(selection.value) && !isHotels(selection.value)) {
      switchModeConfirm(hotel.name, true, apply);
      return;
    }
    apply();
  };

  const removeChip = (chip: SelectionChip): void => {
    setSelection(removeSelectionItem(selection.value, chip));
  };

  const clearAnchor = (): void => {
    anchor.value = null;
  };

  const back = (): boolean => {
    if (view.value === 'country') {
      direction.value = 'left';
      view.value = 'root';
      openedCountry.value = null;
      mode.value = 'resorts';
      query.value = '';
      countryResorts.value = [];
      anchor.value = null;
      clearSearchState();
      return true;
    }
    if (query.value !== '') {
      query.value = '';
      clearSearchState();
      return true;
    }
    return false;
  };

  return {
    view,
    mode,
    direction,
    openedCountry,
    query,
    loading,
    searchLoading,
    selection,
    lockedCountry,
    count,
    chips,
    placeholder,
    recentCountries,
    countryGroups,
    searchSections,
    resortGroups,
    hotelRows,
    checkedResortIds: checkedResortIds as ComputedRef<Set<number>>,
    checkedHotelIds: checkedHotelIds as ComputedRef<Set<number>>,
    allResortsChecked,
    isSearchEmpty,
    anchor,
    confirmState,

    setQuery,
    openCountry,
    pickSearchResult,
    toggleAllResorts,
    toggleResort,
    toggleHotel,
    removeChip,
    confirmPending,
    cancelPending,
    clearAnchor,
    back,
  };
}
